import { useCallback, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calls `onTimeout` once `duration` has elapsed while not paused.
 * Pausing keeps the remaining time, so the countdown resumes where it stopped.
 */
export const useNoticeToastTimer = (params: Params) => {
  const { duration, isPaused, onTimeout } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  const remainingTimeRef = useRef(duration === 'never' ? undefined : duration);
  const onTimeoutRef = useRef(onTimeout);

  onTimeoutRef.current = onTimeout;

  // ─── Handlers ────────────────────────────────────────────────────────

  const startCountdown = useCallback(() => {
    const remainingTime = remainingTimeRef.current;

    if (isPaused || remainingTime === undefined) {
      return;
    }

    const startedAt = Date.now();
    const timeoutId = setTimeout(handleTimeout, remainingTime);

    function handleTimeout() {
      remainingTimeRef.current = undefined;
      onTimeoutRef.current();
    }

    return function pauseCountdown() {
      clearTimeout(timeoutId);

      if (remainingTimeRef.current === undefined) {
        return;
      }

      remainingTimeRef.current = Math.max(0, remainingTime - (Date.now() - startedAt));
    };
  }, [isPaused]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(startCountdown, [startCountdown]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  duration: number | 'never';
  isPaused: boolean;
  onTimeout: () => void;
};
